import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import type { Nullable } from "@msuite/utils/types";
import { client } from "@/hono";

/**
 * The protocol of one picking round, without the order-level transitions around it.
 *
 * A separate request rather than a client-side filter of the order's protocol: the two panels sit
 * on different pages and neither loads the other's data, so narrowing here would mean fetching an
 * order's whole history to show four rows of it.
 */
export function useSuborderStateTransitions(
	orderId: Nullable<string>,
	suborderId: Nullable<string>,
) {
	/** Query */
	const query = useQuery({
		queryKey: ["suborder", guardString(orderId), guardString(suborderId), "state-transitions"],
		queryFn: async () => {
			const response = await client.stock.orders[":orderId"].suborders[":suborderId"][
				"state-transitions"
			].$get({
				param: {
					orderId: guardString(orderId),
					suborderId: guardString(suborderId),
				},
			});
			// Suppressed for the same reason as `useSuborder`: the page renders the refusal itself.
			const data = await responseHandler(response, false);
			return data.stateTransitions;
		},
		enabled: !!orderId && !!suborderId,
	});

	/** Return */
	return { ...query, stateTransitions: query.data };
}
